import type {
    IChosenSiteLegend,
    IData,
    ISharedVisitorsData,
    ISiteItem,
} from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/sharedTypes";
import type { IVennChartData } from "app/pages/website-analysis/audience-overlap/types";
import { percentageSignFilter } from "app/filters/ngFilters";

interface ITableRecord {
    overlappedDomains: ISiteItem[];
    share: number;
    overlappedUnique: number;
    exclusiveUnique: number;
    unreached: number;
}

export interface ITableData {
    Data: ITableRecord[];
}

const SITES_DELIMITER = ",";
const SITES_SUFFIX_DELIMITER = ".";

const getFilterDataBySite = (data: ISharedVisitorsData) => (siteArg) =>
    Object.fromEntries(
        Object.entries(data).filter((entry) => {
            const [key] = entry;
            const sitesArray = key.split(SITES_DELIMITER);
            return sitesArray.some((site) => site === siteArg);
        }),
    );

const buildSiteMetaDataMapping = (
    chosenSitesLegends: IChosenSiteLegend[],
): Record<string, { color: string; isAutoSuggest: boolean }> => {
    return chosenSitesLegends.reduce((res, site) => {
        const metaData = {
            color: site.color,
            isAutoSuggest: site.isAutoSuggest,
        };

        return {
            ...res,
            [site.name]: metaData,
        };
    }, {});
};

export const getTableData = (
    data: IData,
    selectedSite: string,
    chosenSitesLegends: IChosenSiteLegend[],
    vennChartData: IVennChartData,
) => {
    if (!data || !Object.values(data.Data).length) {return [];}

    const siteToMetaDataMapping = buildSiteMetaDataMapping(chosenSitesLegends);
    const filterDataBySite = getFilterDataBySite(data.Data);
    const selectedSiteSharedVisitors: ISharedVisitorsData = filterDataBySite(selectedSite);

    // inclusive visits
    const visits = vennChartData.reduce((visits, { sets, realValue }) => ({ ...visits, [sets.join()]: realValue }), {});

    const selectedSiteVisits = visits[selectedSite];

    const tableData = Object.keys(selectedSiteSharedVisitors)
        // Filter out the table line of the currently selected tabl
        .filter((selectedSiteSharedVisitor) => {
            return selectedSiteSharedVisitor !== selectedSite;
        })
        .map((selectedSiteSharedVisitor) => {
            const currentIntersectionVisits = visits[selectedSiteSharedVisitor];
            const exclusiveUniqueView = data.ExclusiveAudience[selectedSite][selectedSiteSharedVisitor];
            const unreached = data.UnreachedAudience[selectedSite][selectedSiteSharedVisitor];
            return {
                overlappedDomains: selectedSiteSharedVisitor
                    .split(SITES_DELIMITER)
                    // the selected site should be first
                    .sort((site) => (site === selectedSite ? -1 : 1))
                    .map((site) => ({
                        // remove the site suffix, e.g amazon.com => amazon
                        displayText: site.split(SITES_SUFFIX_DELIMITER)[0],
                        color: siteToMetaDataMapping[site].color,
                        displayValue: percentageSignFilter()(visits[selectedSiteSharedVisitor] / visits[site], 1),
                        isAutoSuggest: siteToMetaDataMapping[site].isAutoSuggest,
                    })),
                share: currentIntersectionVisits / selectedSiteVisits,
                overlappedUnique: currentIntersectionVisits,
                exclusiveUnique: exclusiveUniqueView,
                unreached,
            };
        });
    return tableData;
};
